// Add click event listener to all elements with class "click-scroll"
document.querySelectorAll('.click-scroll').forEach(el => {
  el.addEventListener('click', (e) => {
    e.preventDefault();
    const target = el.getAttribute('data-target');
    if (target) {
      const targetEl = document.getElementById(target);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  });
});

// Throttled scroll event listener for sticky header
let scrollTicking = false;
const header = document.querySelector('.header');
window.addEventListener('scroll', () => {
  if (!scrollTicking) {
    requestAnimationFrame(() => {
      if (header) {
        if (window.scrollY > 40) {
          header.classList.add('sticky');
        } else {
          header.classList.remove('sticky');
        }
      }
      scrollTicking = false;
    });
    scrollTicking = true;
  }
}, { passive: true });

// Add change event listener to all elements with class "choose-variant"
document.querySelectorAll('.choose-variant').forEach(el => {
  el.addEventListener('change', (e) => {
    const variant = el.getAttribute('data-variant');
    if (variant) {
      document.querySelectorAll('.link-to-variant').forEach(link => {
        link.classList.remove('show');
      });
      document.querySelectorAll(`.link-to-variant.${variant}`).forEach(link => {
        link.classList.add('show');
      });
    }
  });
});


// Optimized Cart JS
class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.cartCount = document.querySelectorAll('[data-cart-count]');
    this.isOpen = false;
    this.isLoading = false;
    this.errorTimeout = null;
    this.updateDebounceTimer = null;
    this.pendingUpdate = null;
    this.isRefreshing = false;
    this.needsRefresh = false;
    this.isUpdating = false;
    this.isAdding = false;

    this.init();
  }

  init() {
    this.bindEvents();
    this._fetchCartCount();
  }

  bindEvents() {
    // Single delegated event listener for all click events
    document.addEventListener('click', (e) => {
      // Open drawer
      if (e.target.matches('[data-cart-drawer-open]') || e.target.closest('[data-cart-drawer-open]')) {
        e.preventDefault();
        this.open();
        return;
      }

      // Close drawer
      if (e.target.matches('[data-cart-drawer-close]') || e.target.closest('[data-cart-drawer-close]')) {
        e.preventDefault();
        this.close();
        return;
      }

      // Cart update (+/- buttons)
      if (e.target.matches('[data-cart-update]') || e.target.closest('[data-cart-update]')) {
        e.preventDefault();
        const button = e.target.closest('[data-cart-update]');
        const itemId = button.dataset.lineKey || button.dataset.variantId;
        this.updateCart(itemId, button.dataset.quantity);
        return;
      }

      // Cart remove
      if (e.target.matches('[data-cart-remove]') || e.target.closest('[data-cart-remove]')) {
        e.preventDefault();
        const button = e.target.closest('[data-cart-remove]');
        const itemId = button.dataset.lineKey || button.dataset.variantId;
        this.updateCart(itemId, 0);
        return;
      }

      // Clear cart
      if (e.target.matches('[data-cart-clear]') || e.target.closest('[data-cart-clear]')) {
        e.preventDefault();
        this.clearCart();
        return;
      }
    });

    // Form submission for add to cart
    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action*="/cart/add"]')) {
        e.preventDefault();
        this.addToCart(e.target);
      }
    });

    // Quantity input changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-cart-quantity-input], .cart-drawer__quantity-input')) {
        const input = e.target;
        const quantity = parseInt(input.value) || 0;
        const itemId = input.dataset.itemKey || input.dataset.lineKey || input.dataset.variantId;
        this.updateCart(itemId, quantity);
      }
    });

    // Keyboard events
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  // Fast cart count fetch on init
  async _fetchCartCount() {
    try {
      const response = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (response.ok) {
        const cart = await response.json();
        this._updateCountDisplay(cart.item_count);
      }
    } catch (error) {}
  }

  _updateCountDisplay(count) {
    this.cartCount.forEach(el => {
      el.textContent = count || '0';
    });
  }

  async addToCart(form) {
    if (this.isAdding) return;
    this.isAdding = true;

    const submitButton = form.querySelector('button[type="submit"], input[type="submit"]') || form;
    this.setButtonLoading(submitButton, true);

    try {
      const formData = new FormData();
      const idInput = form.querySelector('input[name="id"]');
      const quantityInput = form.querySelector('input[name="quantity"]');

      if (!idInput?.value) {
        throw new Error('Product variant ID is required');
      }

      formData.append('id', idInput.value);
      formData.append('quantity', (parseInt(quantityInput?.value || '1') || 1).toString());

      const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
      if (sellingPlanInput?.value) {
        formData.append('selling_plan', sellingPlanInput.value);
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (response.ok) {
        // Fetch full cart data in parallel with opening drawer
        const [, cartResponse] = await Promise.all([
          this.open(),
          fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        ]);

        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          this._updateCountDisplay(cart.item_count);
          await this._refreshDrawerContent();
          // Savings is already correctly rendered by server via Liquid
        }

        document.dispatchEvent(new CustomEvent('cart:added', { detail: await response.json() }));
      } else {
        const error = await response.json();
        this.showError(error.description || error.message || 'Failed to add item to cart');
      }
    } catch (error) {
      this.showError(error.message || 'Failed to add item to cart');
    } finally {
      this.isAdding = false;
      this.setButtonLoading(submitButton, false);
    }
  }

  updateCart(itemId, quantity) {
    quantity = parseInt(quantity) || 0;

    // Find quantity input - check multiple possible attributes
    const quantityInput = this.drawer?.querySelector(
      `[data-item-key="${itemId}"].cart-drawer__quantity-input, [data-line-key="${itemId}"][data-cart-quantity-input], [data-variant-id="${itemId}"][data-cart-quantity-input]`
    );
    const prevQuantity = quantityInput ? parseInt(quantityInput.value) || 0 : null;

    // Optimistic UI update
    if (quantityInput && quantity > 0) {
      quantityInput.value = quantity;
      const container = quantityInput.closest('.cart-item, .cart-drawer__item, [data-cart-item], [data-item-key]')?.closest('.cart-drawer__item') || quantityInput.closest('.cart-drawer__item');
      if (container) {
        // Update decrease button
        const decreaseBtn = container.querySelector('[data-action="decrease"], [data-cart-update]:first-of-type');
        if (decreaseBtn) decreaseBtn.dataset.quantity = Math.max(0, quantity - 1);

        // Update increase button
        const increaseBtn = container.querySelector('[data-action="increase"], [data-cart-update]:last-of-type');
        if (increaseBtn) increaseBtn.dataset.quantity = quantity + 1;
      }
    }

    // Debounce
    if (this.updateDebounceTimer) clearTimeout(this.updateDebounceTimer);
    this.pendingUpdate = { itemId, quantity, prevQuantity, quantityInput };
    this.updateDebounceTimer = setTimeout(() => this._executeUpdate(this.pendingUpdate), 30);
  }

  async _executeUpdate({ itemId, quantity, prevQuantity, quantityInput }) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: itemId, quantity })
      });

      if (response.ok) {
        const cart = await response.json();
        this._updateCountDisplay(cart.item_count);
        this._updateCartDOM(cart, itemId, quantity);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      } else {
        const error = await response.json();
        if (quantityInput && prevQuantity !== null) quantityInput.value = prevQuantity;
        this.showError(error.description || error.message || 'Failed to update cart');
      }
    } catch (error) {
      if (quantityInput && prevQuantity !== null) quantityInput.value = prevQuantity;
      this.showError('Failed to update cart');
    } finally {
      this.isUpdating = false;
    }
  }

  _updateCartDOM(cart, itemId, quantity) {
    if (!this.drawer) return;

    // Handle item removal
    if (quantity === 0) {
      const itemElement = this.drawer.querySelector(
        `[data-item-key="${itemId}"], [data-line-key="${itemId}"], [data-variant-id="${itemId}"]`
      )?.closest('.cart-drawer__item');

      if (itemElement) itemElement.remove();

      if (cart.item_count === 0) {
        this._showEmptyCart();
        return;
      }
    }

    // Batch DOM updates
    requestAnimationFrame(() => {
      // Update all items
      cart.items.forEach(item => {
        const container = this.drawer.querySelector(
          `[data-item-key="${item.key}"]`
        )?.closest('.cart-drawer__item') || this.drawer.querySelector(
          `[data-line-key="${item.key}"], [data-variant-id="${item.variant_id}"]`
        )?.closest('.cart-drawer__item');

        if (container) {
          // Update quantity input
          const qtyInput = container.querySelector('.cart-drawer__quantity-input, [data-cart-quantity-input]');
          if (qtyInput) qtyInput.value = item.quantity;

          // Update data-quantity attribute for savings calculation
          container.dataset.quantity = item.quantity;

          // Update line price
          const linePrice = container.querySelector('.cart-drawer__item-price--sale, [data-line-price], .cart-item__price');
          if (linePrice) linePrice.textContent = this._formatMoney(item.final_line_price);

          // Update decrease button
          const decreaseBtn = container.querySelector('[data-action="decrease"]');
          if (decreaseBtn) decreaseBtn.dataset.quantity = Math.max(0, item.quantity - 1);

          // Update increase button
          const increaseBtn = container.querySelector('[data-action="increase"]');
          if (increaseBtn) increaseBtn.dataset.quantity = item.quantity + 1;
        }
      });

      // Update totals - include #cart-drawer-subtotal
      const price = this._formatMoney(cart.total_price);
      this.drawer.querySelectorAll('#cart-drawer-subtotal, [data-cart-total], .cart-total, .cart-drawer__total, .cart-drawer__price').forEach(el => {
        el.textContent = price;
      });

      // Update item count text
      this.drawer.querySelectorAll('.item_in_cart').forEach(el => {
        el.textContent = `${cart.item_count} item(s)`;
      });

      // Update free shipping bar
      this._updateFreeShipping(cart.total_price);

      // Update total savings (from compare_at_price data attributes)
      let totalSavings = 0;
      this.drawer.querySelectorAll('.cart-drawer__item[data-compare-at-price]').forEach(itemEl => {
        const compareAtPrice = parseInt(itemEl.dataset.compareAtPrice) || 0;
        const price = parseInt(itemEl.dataset.price) || 0;
        const quantity = parseInt(itemEl.dataset.quantity) || 1;
        if (compareAtPrice > price) {
          totalSavings += (compareAtPrice - price) * quantity;
        }
      });
      const savingsEl = this.drawer.querySelector('#cart-drawer-savings');
      if (savingsEl) {
        if (totalSavings > 0) {
          savingsEl.textContent = `You saved ${this._formatMoney(totalSavings).replace('.00', '')} plus shipping`;
          savingsEl.style.display = '';
        } else {
          savingsEl.style.display = 'none';
        }
      }
    });
  }

  _updateFreeShipping(cartTotalCents) {
    const wrapper = this.drawer?.querySelector('.free-shipping-wrapper[data-free-shipping-threshold]');
    if (!wrapper) return;

    const threshold = parseInt(wrapper.dataset.freeShippingThreshold) || 0;
    const successMsg = wrapper.dataset.successMsg || '🎉 You have unlocked free shipping!';

    if (threshold <= 0) return;

    const remaining = threshold - cartTotalCents;
    const percentage = Math.min(100, (cartTotalCents * 100) / threshold);

    const messageEl = wrapper.querySelector('.free-shipping-message');
    const sliderEl = wrapper.querySelector('.free-shipping-slider');
    const progressBar = wrapper.querySelector('.free-shipping-progress-bar');

    if (cartTotalCents >= threshold) {
      // Free shipping unlocked
      if (messageEl) {
        messageEl.className = 'free-shipping-message success mb-0';
        messageEl.innerHTML = successMsg;
      }
      if (sliderEl) sliderEl.style.display = 'none';
    } else {
      // Still need more
      if (messageEl) {
        messageEl.className = 'free-shipping-message';
        messageEl.innerHTML = `You're only <strong>${this._formatMoney(remaining)}</strong> away from free shipping! 🚚`;
      }
      if (sliderEl) sliderEl.style.display = '';
      if (progressBar) {
        progressBar.style.width = `${percentage}%`;
        progressBar.dataset.progress = percentage;
      }
    }
  }

  _showEmptyCart() {
    // Hide free shipping wrapper
    const freeShippingWrapper = this.drawer?.querySelector('.free-shipping-wrapper');
    if (freeShippingWrapper) {
      freeShippingWrapper.style.display = 'none';
    }

    // Update item count to 0
    document.querySelectorAll('.item_in_cart').forEach(el => {
      el.textContent = '0 item(s)';
    });

    const body = this.drawer?.querySelector('.cart-drawer__body');
    if (body) {
      body.innerHTML = `
        <div class="cart-drawer__empty">
          <svg width="100" height="100" viewBox="0 0 192 192" style="margin-bottom:20px;">
            <path fill="silver" d="M148.86,117.75l-81.1.34c2.83,7.35,9.56,11.23,17.45,11.24l59.49.04c3.34,0,4.87,2.81,6.03,5.56,1.49,3.49-.04,11.27-5.15,11.26l-67.82-.1c-9-.01-16.59-7.26-21.36-14.14s-7.77-14.92-9.45-23.57l-14.63-75.13c-6.53-3.32-19.02-3.53-18.01-13.45.39-3.86,3.38-8.57,8.03-7.83,6.04.97,11.57,3.53,17.34,5.01,8.74,2.23,6.94,8.75,10.37,15.94l52.61-.42,61.59-.42c1.6-.01,2.88,1.06,4.64.85,2.75-.33,5.67,1.26,7.12,3.03s2.61,5.47,1.79,8.2l-18.93,63.85c-1.22,4.12-5.12,9.71-10.03,9.74Z"/>
          </svg>
          <h3>Your cart is empty</h3>
          <p>Add some products to get started</p>
          <a href="/collections/all" class="btn btn-green">Shop Now</a>
        </div>`;
    }
  }

  _formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    if (window.theme?.Currency?.formatMoney) return window.theme.Currency.formatMoney(cents);
    return '£' + (cents / 100).toFixed(2);
  }

  // Non-blocking drawer refresh (used only for add to cart)
  async _refreshDrawerContent() {
    if (this.isRefreshing) {
      this.needsRefresh = true;
      return;
    }
    this.isRefreshing = true;

    try {
      const response = await fetch('/cart?view=drawer', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newDrawer = doc.getElementById('cart-drawer');

        if (newDrawer && this.drawer) {
          this.drawer.innerHTML = newDrawer.innerHTML;
          if (typeof initUpsellSlider === 'function') initUpsellSlider();
        }
      }
    } catch (error) {}
    finally {
      this.isRefreshing = false;
      if (this.needsRefresh) {
        this.needsRefresh = false;
        this._refreshDrawerContent();
      }
    }
  }

  open() {
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    this.isOpen = true;
    document.body.style.overflow = 'hidden';
  }

  close() {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    this.isOpen = false;
    document.body.style.overflow = '';
  }

  setButtonLoading(button, loading) {
    if (!button) return;
    button.classList.toggle('loading', loading);
    button.disabled = loading;
  }

  showError(message) {
    const errorElement = document.querySelector('.error-msg');
    if (errorElement) {
      if (this.errorTimeout) clearTimeout(this.errorTimeout);
      errorElement.textContent = message;
      errorElement.style.display = 'block';
      this.errorTimeout = setTimeout(() => {
        errorElement.style.display = 'none';
        errorElement.textContent = '';
      }, 5000);
    }
  }

  async clearCart() {
    if (this.isLoading) return;
    this.isLoading = true;

    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (response.ok) {
        const cart = await response.json();
        this._updateCountDisplay(0);
        this._updateFreeShipping(0);
        this._showEmptyCart();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      } else {
        const error = await response.json();
        this.showError(error.description || error.message || 'Failed to clear cart');
      }
    } catch (error) {
      this.showError('Failed to clear cart');
    } finally {
      this.isLoading = false;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartDrawer());
} else {
  new CartDrawer();
}

// Export
if (typeof window !== 'undefined') {
  window.CartDrawer = CartDrawer;
}
